import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  getAllCommissionProgress, getCommissionProgressByAgentId, getCommissionProgressByGroupName,
} from '../../api/commissionProgressApi';
import { getAllExtraSlips, getExtraSlipsByAgent, getExtraSlipsByGroupName } from '../../api/extraSlipApi';
import { getAllIncentiveSlips, getIncentiveSlipsByAgentId, getIncentiveSlipsByGroupName } from '../../api/incentiveSlipApi';
import { getAllAgentGroups } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";

const TYPE_COLORS = {
  PSC: '#1A8CFF',
  DOC: '#10B981',
  TOC: '#F59E0B',
  MOC: '#8B5CF6',
  Slips: '#F97316',
};

const DATE_PRESETS = [
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
  { label: 'All Time', start: () => null, end: () => null },
];

// ─── Type buckets ───────────────────────────────────────────────────────────
const PSC_TYPES = ['PSC', 'PSC/2'];
const DOC_TYPES = ['DOC', 'DOC/2'];
const TOC_TYPES = ['TOC', 'TOC/2'];
const MOC_TYPES = ['MOC', 'MOC/2'];
const ALL_AGENT_TYPES = [...PSC_TYPES, ...DOC_TYPES, ...TOC_TYPES, ...MOC_TYPES];
const TOTAL_COMM_TYPE = 'Total Commission';

// ─── Sum payments on a progress row whose payment date falls within range ──
const paymentsInRange = (cd, start, end) => {
  if (!cd) return 0;
  const slots = [
    [cd.firstPayment, cd.firstPaymentOn],
    [cd.secondPayment, cd.secondPaymentOn],
    [cd.repayment, cd.repaymentOn],
    [cd.repayment2, cd.repaymentOn2],
    [cd.repayment3, cd.repaymentOn3],
    [cd.repayment4, cd.repaymentOn4],
    [cd.repayment5, cd.repaymentOn5],
  ];
  let sum = 0;
  for (const [amt, on] of slots) {
    if (!amt || !on) continue;
    const d = dayjs(on);
    if (start && d.isBefore(start, 'day')) continue;
    if (end && d.isAfter(end, 'day')) continue;
    sum += amt;
  }
  return sum;
};

const StatCard = ({ label, value, sub, borderColor }) => (
  <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px' }}>
    <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

export default function PayoutReport() {
  const { user } = useUser();
  const navigate = useNavigate();

  // Role-driven scope (matches Sales / Commission Reports).
  const roles = user?.roles || [];
  const isAccountantOrSuper = roles.includes('Accountant') || roles.includes('Super Admin');
  const hasAny = (list) => roles.some(r => list.includes(r));
  const availableScopes = useMemo(() => {
    const t = [];
    if (hasAny(['Sales Agent', 'In-house Agent', 'Leader', 'Accountant', 'Super Admin'])) t.push({ key: 'personal', label: 'Personal' });
    if (hasAny(['Leader', 'Accountant', 'Super Admin']))                                  t.push({ key: 'group',    label: 'Group' });
    if (hasAny(['Accountant', 'Super Admin']))                                            t.push({ key: 'all',      label: 'All' });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [scopeTab, setScopeTab] = useState(availableScopes[0]?.key || '');
  useEffect(() => {
    if (!scopeTab && availableScopes.length > 0) setScopeTab(availableScopes[0].key);
  }, [availableScopes, scopeTab]);

  const [groups, setGroups] = useState([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  useEffect(() => {
    if (!isAccountantOrSuper) return;
    let cancelled = false;
    getAllAgentGroups().then(r => {
      if (cancelled) return;
      const list = r.data || [];
      setGroups(list);
      if (!selectedGroupName && list.length > 0) setSelectedGroupName(list[0].groupName || list[0].name || '');
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountantOrSuper]);

  const groupNameForQuery = isAccountantOrSuper ? selectedGroupName : (user?.groupName || '');

  const [progress, setProgress] = useState([]);
  const [extraSlips, setExtraSlips] = useState([]);
  const [incentiveSlips, setIncentiveSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [activePreset, setActivePreset] = useState('This Month');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [agentSearch, setAgentSearch] = useState('');

  const fetchData = useCallback(async () => {
    if (!scopeTab) { setLoading(false); return; }
    setLoading(true);
    try {
      let progRes, extraRes, incRes;
      if (scopeTab === 'all') {
        [progRes, extraRes, incRes] = await Promise.all([
          getAllCommissionProgress(),
          getAllExtraSlips(),
          getAllIncentiveSlips(),
        ]);
      } else if (scopeTab === 'group') {
        if (!groupNameForQuery) {
          setProgress([]); setExtraSlips([]); setIncentiveSlips([]);
          setLoading(false); return;
        }
        [progRes, extraRes, incRes] = await Promise.all([
          getCommissionProgressByGroupName(groupNameForQuery),
          getExtraSlipsByGroupName(groupNameForQuery),
          getIncentiveSlipsByGroupName(groupNameForQuery),
        ]);
      } else {
        [progRes, extraRes, incRes] = await Promise.all([
          getCommissionProgressByAgentId(user?.userId),
          getExtraSlipsByAgent(user?.userId),
          getIncentiveSlipsByAgentId(user?.userId),
        ]);
      }
      setProgress(progRes?.data || []);
      setExtraSlips(Array.isArray(extraRes) ? extraRes : (extraRes?.data || []));
      setIncentiveSlips(incRes?.data || []);
    } catch (err) {
      console.error('Error loading payout report:', err);
      setProgress([]); setExtraSlips([]); setIncentiveSlips([]);
    } finally {
      setLoading(false);
    }
  }, [scopeTab, groupNameForQuery, user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  // ─── In: Astern claim payments within range ──────────────────────────────
  const totalIn = useMemo(() =>
    progress
      .filter(r => r.commissionProgressDto?.commPayType === TOTAL_COMM_TYPE)
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );

  // ─── Out: per-type aggregates ────────────────────────────────────────────
  const sumByTypes = useCallback((types) =>
    progress
      .filter(r => types.includes(r.commissionProgressDto?.commPayType))
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );

  const pscTotal = useMemo(() => sumByTypes(PSC_TYPES), [sumByTypes]);
  const docTotal = useMemo(() => sumByTypes(DOC_TYPES), [sumByTypes]);
  const tocTotal = useMemo(() => sumByTypes(TOC_TYPES), [sumByTypes]);
  const mocTotal = useMemo(() => sumByTypes(MOC_TYPES), [sumByTypes]);

  // ─── Slips: approved Incentive + Extra in date range ─────────────────────
  const slipDateInRange = (date) => {
    if (!date) return false;
    const d = dayjs(date);
    if (startDate && d.isBefore(startDate, 'day')) return false;
    if (endDate && d.isAfter(endDate, 'day')) return false;
    return true;
  };

  const slipsTotal = useMemo(() => {
    const inc = incentiveSlips
      .filter(s => s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.totalPayout || 0), 0);
    const extra = extraSlips
      .filter(s => s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.amount || 0), 0);
    return inc + extra;
  }, [incentiveSlips, extraSlips, startDate, endDate]);

  const totalOut = pscTotal + docTotal + tocTotal + mocTotal + slipsTotal;
  const netRetained = totalIn - (pscTotal + docTotal + tocTotal + mocTotal); // matches Webbey Commission

  // ─── Tan Jin Sheng's earnings (for "what if TJS were kept by company") ──
  // Hardcoded id; if TJS's userId differs in prod, just bump the constant.
  const TJS_USER_ID = 30;

  const tjsByTypes = useCallback((types) =>
    progress
      .filter(r => r.commissionProgressDto?.paymentTo === TJS_USER_ID
                && types.includes(r.commissionProgressDto?.commPayType))
      .reduce((s, r) => s + paymentsInRange(r.commissionProgressDto, startDate, endDate), 0),
    [progress, startDate, endDate]
  );
  const tjsPsc = useMemo(() => tjsByTypes(PSC_TYPES), [tjsByTypes]);
  const tjsDoc = useMemo(() => tjsByTypes(DOC_TYPES), [tjsByTypes]);
  const tjsToc = useMemo(() => tjsByTypes(TOC_TYPES), [tjsByTypes]);
  const tjsMoc = useMemo(() => tjsByTypes(MOC_TYPES), [tjsByTypes]);
  const tjsOverrides = tjsPsc + tjsDoc + tjsToc + tjsMoc;

  const tjsSlips = useMemo(() => {
    const inc = incentiveSlips
      .filter(s => s.agentId === TJS_USER_ID && s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.totalPayout || 0), 0);
    const extra = extraSlips
      .filter(s => s.agentId === TJS_USER_ID && s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued))
      .reduce((sum, s) => sum + (s.amount || 0), 0);
    return inc + extra;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentiveSlips, extraSlips, startDate, endDate]);
  const tjsTotal = tjsOverrides + tjsSlips;

  // ─── Profit ratios + TJS-combined views ─────────────────────────────────
  const pctOfIn = (v) => totalIn > 0 ? ((v / totalIn) * 100).toFixed(1) + '%' : '—';
  const profitMarginPct = totalIn > 0 ? ((netRetained / totalIn) * 100).toFixed(1) + '%' : '—';
  const commissionRatioPct = totalIn > 0 ? ((totalOut / totalIn) * 100).toFixed(1) + '%' : '—';
  const tjsTakePct = pctOfIn(tjsTotal);

  const netInclTjsPsc = netRetained + tjsPsc;
  const netInclTjsOverrides = netRetained + tjsOverrides;
  const netInclTjsAll = netRetained + tjsTotal;

  // ─── Monthly trend for In vs Out ─────────────────────────────────────────
  const monthlyTrend = useMemo(() => {
    const map = {};
    const bumpProgress = (types, key) => {
      progress.filter(r => types.includes(r.commissionProgressDto?.commPayType)).forEach(r => {
        const cd = r.commissionProgressDto;
        if (!cd) return;
        const slots = [
          [cd.firstPayment, cd.firstPaymentOn],
          [cd.secondPayment, cd.secondPaymentOn],
          [cd.repayment, cd.repaymentOn],
          [cd.repayment2, cd.repaymentOn2],
          [cd.repayment3, cd.repaymentOn3],
          [cd.repayment4, cd.repaymentOn4],
          [cd.repayment5, cd.repaymentOn5],
        ];
        for (const [amt, on] of slots) {
          if (!amt || !on) continue;
          const d = dayjs(on);
          if (startDate && d.isBefore(startDate, 'day')) continue;
          if (endDate && d.isAfter(endDate, 'day')) continue;
          const m = d.format('MMM YYYY');
          if (!map[m]) map[m] = { name: m, In: 0, Out: 0 };
          map[m][key] += amt;
        }
      });
    };
    bumpProgress([TOTAL_COMM_TYPE], 'In');
    bumpProgress(ALL_AGENT_TYPES, 'Out');

    // Slips → Out
    const bumpSlip = (date, amount) => {
      if (!date || !amount) return;
      const d = dayjs(date);
      if (startDate && d.isBefore(startDate, 'day')) return;
      if (endDate && d.isAfter(endDate, 'day')) return;
      const m = d.format('MMM YYYY');
      if (!map[m]) map[m] = { name: m, In: 0, Out: 0 };
      map[m].Out += amount;
    };
    incentiveSlips.filter(s => s.status === 'Approved').forEach(s => bumpSlip(s.approvedOn ?? s.dateIssued, s.totalPayout));
    extraSlips.filter(s => s.status === 'Approved').forEach(s => bumpSlip(s.approver2Date ?? s.dateIssued, s.amount));

    return Object.values(map).sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [progress, incentiveSlips, extraSlips, startDate, endDate]);

  // ─── Agent breakdown (per recipient) ─────────────────────────────────────
  // Build candidate set: every user who has ever been a paymentTo on an agent-type
  // row, or an agentId on any slip. This way every possible commission earner is
  // visible — period totals show 0 when they earned nothing in range.
  const agentBreakdown = useMemo(() => {
    const map = {};
    const ensure = (id, name) => {
      if (!id) return null;
      if (!map[id]) map[id] = { paymentTo: id, name: name || 'Unknown', psc: 0, doc: 0, toc: 0, moc: 0, slips: 0, total: 0 };
      // Refresh name if the previous entry was 'Unknown' but a better one came in
      else if (name && map[id].name === 'Unknown') map[id].name = name;
      return map[id];
    };

    // Seed: every agent-type progress recipient (any date)
    progress.forEach(r => {
      const cd = r.commissionProgressDto;
      const t = cd?.commPayType;
      if (!t || !ALL_AGENT_TYPES.includes(t)) return;
      ensure(cd.paymentTo, cd.paymentToName);
    });
    // Seed: every slip recipient
    incentiveSlips.forEach(s => ensure(s.agentId, s.agentName));
    extraSlips.forEach(s => ensure(s.agentId, s.agentName));

    // Fill period values
    progress.forEach(r => {
      const cd = r.commissionProgressDto;
      const t = cd?.commPayType;
      if (!t || !ALL_AGENT_TYPES.includes(t)) return;
      const id = cd.paymentTo;
      if (!id) return;
      const paid = paymentsInRange(cd, startDate, endDate);
      if (paid <= 0) return;
      const row = ensure(id, cd.paymentToName);
      if (PSC_TYPES.includes(t)) row.psc += paid;
      else if (DOC_TYPES.includes(t)) row.doc += paid;
      else if (TOC_TYPES.includes(t)) row.toc += paid;
      else if (MOC_TYPES.includes(t)) row.moc += paid;
      row.total += paid;
    });

    incentiveSlips.filter(s => s.status === 'Approved' && slipDateInRange(s.approvedOn ?? s.dateIssued)).forEach(s => {
      const row = ensure(s.agentId, s.agentName);
      if (!row) return;
      row.slips += (s.totalPayout || 0);
      row.total += (s.totalPayout || 0);
    });
    extraSlips.filter(s => s.status === 'Approved' && slipDateInRange(s.approver2Date ?? s.dateIssued)).forEach(s => {
      const row = ensure(s.agentId, s.agentName);
      if (!row) return;
      row.slips += (s.amount || 0);
      row.total += (s.amount || 0);
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [progress, incentiveSlips, extraSlips, startDate, endDate]);

  // Filter by name search
  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agentBreakdown;
    return agentBreakdown.filter(a => (a.name || '').toLowerCase().includes(q));
  }, [agentBreakdown, agentSearch]);

  // Reset page when filter or date changes
  useEffect(() => { setPage(0); }, [agentSearch, startDate, endDate]);

  if (loading) return <div>Loading...</div>;

  if (availableScopes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Payout Report</h2>
        <Typography sx={{ fontFamily: FONT, color: '#64748B', mt: 1 }}>
          Your role does not have access to any Payout Report view.
        </Typography>
      </Box>
    );
  }

  const scopeSubtitle = scopeTab === 'all'
    ? 'Cash in vs cash out across all groups · filtered by payment date'
    : scopeTab === 'group'
    ? `Group payouts — ${groupNameForQuery || 'your group'} · filtered by payment date`
    : 'Your personal payouts · filtered by payment date';

  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  return (
    <div>
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Payout Report</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>{scopeSubtitle}</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      {/* Scope tabs (Personal / Group / All) */}
      {availableScopes.length > 1 && (
        <Box className="no-print" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive value={scopeTab} onChange={(_, v) => v && setScopeTab(v)} size="small"
            sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.78rem', textTransform: 'none', py: 0.6, px: 1.5 },
                  '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
            {availableScopes.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
          </ToggleButtonGroup>
          {scopeTab === 'group' && isAccountantOrSuper && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={selectedGroupName} onChange={(e) => setSelectedGroupName(e.target.value)}
                displayEmpty sx={{ fontFamily: FONT, fontSize: '0.82rem', borderRadius: '8px' }}>
                {groups.length === 0 && <MenuItem value="" disabled>No groups</MenuItem>}
                {groups.map(g => {
                  const name = g.groupName || g.name || '';
                  return <MenuItem key={name} value={name} sx={{ fontFamily: FONT, fontSize: '0.82rem' }}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      {/* Headline */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="Total In (from Astern)" value={handlePriceWithRM(totalIn)} sub="Astern claim payments" borderColor="#10B981" />
        <StatCard label="Total Out (to Agents + Slips)" value={handlePriceWithRM(totalOut)} sub="PSC + DOC + TOC + MOC + Slips" borderColor="#EF4444" />
        <StatCard label="Net Retained (= Webbey Comm)" value={handlePriceWithRM(netRetained)} sub="In − (PSC + DOC + TOC + MOC)" borderColor="#1A8CFF" />
        <StatCard label="Slips Paid" value={handlePriceWithRM(slipsTotal)} sub="Incentive + Extra slips" borderColor="#F97316" />
      </Box>

      {/* Per-type breakdown */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="PSC Paid" value={handlePriceWithRM(pscTotal)} borderColor={TYPE_COLORS.PSC} />
        <StatCard label="DOC Paid" value={handlePriceWithRM(docTotal)} borderColor={TYPE_COLORS.DOC} />
        <StatCard label="TOC Paid" value={handlePriceWithRM(tocTotal)} borderColor={TYPE_COLORS.TOC} />
        <StatCard label="MOC Paid" value={handlePriceWithRM(mocTotal)} borderColor={TYPE_COLORS.MOC} />
        <StatCard label="Slips Paid" value={handlePriceWithRM(slipsTotal)} borderColor={TYPE_COLORS.Slips} />
      </Box>

      {/* Profit ratios */}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: FONT, mb: 1 }}>
        Profitability
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="Profit Margin" value={profitMarginPct} sub="Net Retained ÷ Total In" borderColor="#1A8CFF" />
        <StatCard label="Commission Ratio" value={commissionRatioPct} sub="Total Out ÷ Total In" borderColor="#EF4444" />
        <StatCard label="TJS Total Earnings" value={handlePriceWithRM(tjsTotal)} sub="PSC + DOC + TOC + MOC + slips" borderColor="#8B5CF6" />
        <StatCard label="TJS Take" value={tjsTakePct} sub="TJS earnings ÷ Total In" borderColor="#8B5CF6" />
      </Box>

      {/* TJS-combined comparison */}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: FONT, mb: 1 }}>
        Net Retained — what if TJS earnings stayed with company
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="Net (current, excl TJS)" value={handlePriceWithRM(netRetained)} sub={`${pctOfIn(netRetained)} of Total In`} borderColor="#1A8CFF" />
        <StatCard label="+ TJS PSC" value={handlePriceWithRM(netInclTjsPsc)} sub={`${pctOfIn(netInclTjsPsc)} · adds RM ${tjsPsc.toFixed(2)}`} borderColor="#0EA5E9" />
        <StatCard label="+ TJS Overrides" value={handlePriceWithRM(netInclTjsOverrides)} sub={`${pctOfIn(netInclTjsOverrides)} · PSC+DOC+TOC+MOC`} borderColor="#0284C7" />
        <StatCard label="+ TJS Everything" value={handlePriceWithRM(netInclTjsAll)} sub={`${pctOfIn(netInclTjsAll)} · incl. slips`} borderColor="#0369A1" />
      </Box>

      {/* Monthly In vs Out */}
      <Card sx={{ borderRadius: '14px', mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Monthly Cashflow (In vs Out)</Typography>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyTrend} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                <YAxis tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                <Tooltip formatter={v => handlePriceWithRM(v)} />
                <Legend />
                <Bar dataKey="In" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Out" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>
          )}
        </CardContent>
      </Card>

      {/* Agent breakdown */}
      <Card sx={{ borderRadius: '14px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', fontFamily: FONT }}>
              Agent Breakdown
              <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#64748B', fontWeight: 400 }}>
                ({filteredAgents.length} of {agentBreakdown.length} agents)
              </Typography>
            </Typography>
            <TextField
              size="small"
              placeholder="Search agent…"
              value={agentSearch}
              onChange={(e) => setAgentSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                sx: { fontFamily: FONT, fontSize: '0.85rem', borderRadius: '8px' },
              }}
              sx={{ width: 220 }}
            />
          </Box>
          {filteredAgents.length > 0 ? (
            <>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0', backgroundColor: '#F8FAFC' } }}>
                      <TableCell>#</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell align="right">PSC</TableCell>
                      <TableCell align="right">DOC</TableCell>
                      <TableCell align="right">TOC</TableCell>
                      <TableCell align="right">MOC</TableCell>
                      <TableCell align="right">Slips</TableCell>
                      <TableCell align="right">Total Payout</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAgents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((a, i) => {
                      const rowNum = page * rowsPerPage + i + 1;
                      return (
                        <TableRow key={a.paymentTo} hover
                          onClick={() => a.paymentTo && navigate(`/reports/commission/agent/${a.paymentTo}`)}
                          sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{rowNum}</TableCell>
                          <TableCell sx={{ fontWeight: 500, color: '#1A8CFF' }}>{a.name}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.psc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.doc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.toc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.moc)}</TableCell>
                          <TableCell align="right">{handlePriceWithRM(a.slips)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{handlePriceWithRM(a.total)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredAgents.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  borderTop: '1px solid #E2E8F0',
                  '& .MuiTablePagination-toolbar': {
                    fontFamily: FONT,
                    fontSize: '0.78rem',
                    minHeight: 44,
                    paddingLeft: 1,
                    paddingRight: 1,
                    flexWrap: 'wrap',
                    rowGap: 0.5,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontFamily: FONT,
                    fontSize: '0.78rem',
                  },
                  '& .MuiTablePagination-spacer': { display: 'none' },
                  '& .MuiTablePagination-actions': { marginLeft: 'auto' },
                }}
              />
            </>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No agents match the search</Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
